<script>
import axios from 'axios';
import { levelColor } from './severity';

const STATUS_LABELS = {
    new: 'label-danger',
    regressed: 'label-warning',
    acknowledged: 'label-info',
    resolved: 'label-success',
};

/**
 * Mirror of Fabrico\Doppler\Enums\IssueStatus::validTransitions(): only the
 * moves an operator can make. Nothing reaches `regressed` from here — a
 * recurrence is what regresses a resolved issue, not a click.
 */
const ALLOWED_TRANSITIONS = {
    new: ['acknowledged', 'resolved'],
    acknowledged: ['resolved'],
    resolved: [],
    regressed: ['acknowledged', 'resolved'],
};

const TRANSITION_LABELS = {
    acknowledged: 'Acknowledge',
    resolved: 'Resolve',
};

export default {
    name: 'IssuesPanel',
    props: {
        issuesUrl: { type: String, required: true },
        issueUrl: { type: String, required: true },
    },
    emits: ['open-trace'],
    data() {
        return {
            loading: false,
            sinceHours: 168,
            openOnly: false,
            issues: [],
            detail: null,
            detailLoading: false,
            updating: false,
            actionError: null,
        };
    },
    computed: {
        availableTransitions() {
            return (ALLOWED_TRANSITIONS[this.detail?.issue?.status] ?? []).map((status) => ({
                status,
                label: TRANSITION_LABELS[status] ?? status,
            }));
        },
    },
    created() {
        this.refresh();
    },
    methods: {
        levelColor,
        statusClass(status) {
            return STATUS_LABELS[status] ?? 'label-default';
        },
        async refresh() {
            this.loading = true;
            try {
                const { data } = await axios.get(this.issuesUrl, {
                    params: { since_hours: this.sinceHours, open_only: this.openOnly ? 1 : 0 },
                });
                this.issues = data.issues;
            } finally {
                this.loading = false;
            }
        },
        async openFingerprint(fingerprint) {
            this.detailLoading = true;
            this.actionError = null;
            try {
                const { data } = await axios.get(this.issueUrl, { params: { fingerprint } });
                this.detail = data;
            } finally {
                this.detailLoading = false;
            }
        },
        async applyTransition(status) {
            this.updating = true;
            this.actionError = null;

            try {
                // Same resource as the detail endpoint, different verb.
                const { data } = await axios.patch(this.issueUrl, {
                    fingerprint: this.detail.issue.fingerprint,
                    status,
                });

                this.detail.issue = data.issue;
                await this.refresh();
            } catch (e) {
                this.actionError = e.response?.data?.message ?? 'Could not update the issue.';
            } finally {
                this.updating = false;
            }
        },
        formatTs(ts) {
            return ts ? ts.replace('T', ' ').replace(/\+.*$/, '') : '';
        },
    },
};
</script>

<template>
    <div class="logs-issues">
        <div class="form-inline" style="margin-bottom: 10px">
            <select v-model.number="sinceHours" class="form-control input-sm" @change="refresh">
                <option :value="24">Last 24 hours</option>
                <option :value="168">Last 7 days</option>
                <option :value="720">Last 30 days</option>
            </select>
            <label class="checkbox-inline" style="margin-left: 10px">
                <input v-model="openOnly" type="checkbox" @change="refresh" /> Open only
            </label>
            <button class="btn btn-default btn-sm" style="margin-left: 10px" :disabled="loading" @click="refresh">
                <i class="fa" :class="loading ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
            </button>
        </div>

        <div class="table-responsive">
            <table class="table table-condensed table-hover">
                <thead>
                    <tr>
                        <th style="width: 90px">Status</th>
                        <th style="width: 90px">Level</th>
                        <th>Issue</th>
                        <th style="width: 100px" class="text-right">Events</th>
                        <th style="width: 165px">Last seen (UTC)</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="issue in issues"
                        :key="issue.fingerprint"
                        style="cursor: pointer"
                        @click="openFingerprint(issue.fingerprint)"
                    >
                        <td><span class="label" :class="statusClass(issue.status)">{{ issue.status }}</span></td>
                        <td>
                            <span class="label" :style="{ background: levelColor(issue.level) }">{{ issue.level }}</span>
                        </td>
                        <td>
                            <strong v-if="issue.exception_class"><code>{{ issue.exception_class }}</code></strong>
                            <span class="logs-issue-message">{{ issue.sample_message }}</span>
                        </td>
                        <td class="text-right"><strong>{{ issue.occurrences }}</strong></td>
                        <td class="text-muted">{{ formatTs(issue.last_seen_at) }}</td>
                    </tr>
                    <tr v-if="!issues.length && !loading">
                        <td colspan="5" class="text-center text-muted" style="padding: 30px">
                            No issues in the selected window. 🎉
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div v-if="detail" class="box box-solid" style="margin-top: 10px">
            <div class="box-header with-border">
                <strong>
                    <code>{{ detail.issue.fingerprint.slice(0, 16) }}…</code>
                </strong>
                <span class="label" :class="statusClass(detail.issue.status)">{{ detail.issue.status }}</span>
                <span class="text-muted">
                    first seen {{ formatTs(detail.issue.first_seen_at) }} ·
                    {{ detail.issue.occurrences }} events
                    <template v-if="detail.issue.resolved_at">
                        · resolved {{ formatTs(detail.issue.resolved_at) }}
                        <template v-if="detail.issue.resolved_by_name">by {{ detail.issue.resolved_by_name }}</template>
                    </template>
                </span>
                <button class="btn btn-xs btn-default pull-right" @click="detail = null">
                    <i class="fa fa-times"></i>
                </button>
                <span class="pull-right" style="margin-right: 8px">
                    <button
                        v-for="action in availableTransitions"
                        :key="action.status"
                        class="btn btn-xs btn-primary"
                        style="margin-left: 4px"
                        :disabled="updating"
                        @click="applyTransition(action.status)"
                    >
                        {{ action.label }}
                    </button>
                </span>
            </div>
            <div class="box-body">
                <div v-if="actionError" class="alert alert-danger">{{ actionError }}</div>

                <table class="table table-condensed">
                    <tbody>
                        <tr v-for="entry in detail.recent_entries" :key="entry.id">
                            <td style="width: 165px" class="text-muted">{{ formatTs(entry.ts_utc) }}</td>
                            <td>{{ entry.message }}</td>
                            <td style="width: 140px">
                                <a
                                    v-if="entry.correlation_id"
                                    href="#"
                                    @click.prevent="$emit('open-trace', entry.correlation_id)"
                                >
                                    <code>{{ entry.correlation_id }}</code>
                                </a>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped>
.logs-issue-message {
    display: block;
    font-size: 12px;
    color: #666;
    word-break: break-word;
}
</style>
